/**
 * src/pages/admin/AdminNotifications.js
 * 
 * Admin Notification Management page.
 * Send announcements, targeted messages, role-based messages, and popup notifications.
 * View sent notifications with stats (read, dismissed, feedback).
 * Admin can send messages "as" different roles/personas.
 */

import React, { useEffect, useState, useCallback } from "react";
import {
  Bell,
  Send,
  Users,
  Megaphone,
  MessageCircle,
  Eye,
  EyeOff,
  Clock,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Search,
  ChevronDown,
  X,
  UserCheck,
  Star,
  Trash2,
} from "lucide-react";
import * as adminService from "../../services/adminService";
import { useToast } from "../../contexts/ToastContext";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import { useAdmin } from "../../hooks/useAdmin";
import { errorDev } from '../../utils/logger.js';

// ─── Notification Type Tabs ─────────────────────────────────────
const NOTIF_TYPES = [
  { id: "announcement", label: "Announcement", Icon: Megaphone, description: "Visible to all users" },
  { id: "targeted", label: "Targeted", Icon: UserCheck, description: "Sent to specific users" },
  { id: "role_message", label: "Role Message", Icon: MessageCircle, description: "Send as a persona/role" },
  { id: "popup", label: "Pop-up Alert", Icon: AlertTriangle, description: "Urgent in-app popup" },
];

const PRIORITIES = [
  { id: "low", label: "Low", color: "text-gray-500" },
  { id: "normal", label: "Normal", color: "text-blue-500" },
  { id: "high", label: "High", color: "text-amber-500" },
  { id: "urgent", label: "Urgent", color: "text-red-500" },
];

const ANNOUNCEMENT_THEMES = [
  { id: "green", label: "Green (normal)", swatch: "bg-green-600" },
  { id: "blue", label: "Blue (new feature)", swatch: "bg-blue-600" },
  { id: "red", label: "Red (warning)", swatch: "bg-red-600" },
];

const ROLE_PRESETS = [
  { id: "Orrith Team", label: "Orrith Team" },
  { id: "Support Team", label: "Support Team" },
  { id: "Community Manager", label: "Community Manager" },
  { id: "FarmPulse Bot", label: "FarmPulse Bot" },
  { id: "Admin", label: "Admin" },
];

const TARGET_ROLES = [
  { id: "all", label: "All Users" },
  { id: "user", label: "Regular Users" },
  { id: "admin", label: "Admins Only" },
];

// ─── User Search for Targeted Notifications ─────────────────────
function UserSearch({ selectedUsers, onAdd, onRemove }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const handleSearch = useCallback(async (term) => {
    if (term.length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const users = await adminService.findUsers(term, 10);
      setResults(users.filter(u => !selectedUsers.find(s => s.id === u.id)));
    } catch (err) {
      errorDev("Operation failed:", err);
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, [selectedUsers]);

  useEffect(() => {
    const timer = setTimeout(() => handleSearch(query), 300);
    return () => clearTimeout(timer);
  }, [query, handleSearch]);

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search users by email or name…"
          className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition"
        />
        {searching && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 orrith-loader orrith-loader-xs orrith-loader-primary" />
        )}
      </div>

      {/* Search Results */}
      {results.length > 0 && (
        <div className="border border-gray-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 max-h-40 overflow-y-auto">
          {results.map((user) => (
            <button
              key={user.id}
              onClick={() => {
                onAdd(user);
                setQuery("");
                setResults([]);
              }}
              className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 dark:hover:bg-neutral-700 text-left text-sm transition"
            >
              <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-xs font-bold text-green-700 dark:text-green-400">
                {(user.displayName || user.email || "?").charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 dark:text-white truncate">
                  {user.displayName || user.name || "Unknown"}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Selected Users */}
      {selectedUsers.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedUsers.map((user) => (
            <span
              key={user.id}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-100 dark:bg-green-900/30 text-xs font-medium text-green-700 dark:text-green-400"
            >
              {user.displayName || user.email?.split("@")[0]}
              <button onClick={() => onRemove(user.id)} className="hover:text-red-500 transition">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Notification Form ──────────────────────────────────────────
function NotificationForm({ onSent }) {
  const toast = useToast();
  const [type, setType] = useState("announcement");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [priority, setPriority] = useState("normal");
  const [targetRole, setTargetRole] = useState("all");
  const [senderAlias, setSenderAlias] = useState("Orrith Team");
  const [theme, setTheme] = useState("green");
  const [iconName, setIconName] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [requiresFeedback, setRequiresFeedback] = useState(false);
  const [feedbackPrompt, setFeedbackPrompt] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!title.trim() || !message.trim()) {
      toast.warning("Please fill in title and message");
      return;
    }
    if (type === "targeted" && selectedUsers.length === 0) {
      toast.warning("Please select at least one target user");
      return;
    }

    setSending(true);
    try {
      await adminService.withAdminRetry(() => adminService.createAdminNotification(adminUser, {
        title: title.trim(),
        message: message.trim(),
        type,
        targetUserIds: selectedUsers.map(u => u.id),
        targetRole,
        priority,
        theme,
        iconName: iconName.trim(),
        imageUrl: imageUrl.trim(),
        expiresAt: expiresAt ? new Date(expiresAt) : null,
        requiresFeedback,
        feedbackPrompt: feedbackPrompt.trim(),
        senderAlias,
      }));

      toast.success("Notification sent successfully!");
      setTitle("");
      setMessage("");
      setSelectedUsers([]);
      setTheme("green");
      setIconName("");
      setImageUrl("");
      setExpiresAt("");
      setRequiresFeedback(false);
      setFeedbackPrompt("");
      onSent?.();
    } catch (err) {
      toast.error(`Failed to send: ${err.message}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-xl border border-gray-200 dark:border-neutral-700 overflow-hidden">
      {/* Type Selector */}
      <div className="border-b border-gray-200 dark:border-neutral-700 p-3 overflow-x-auto">
        <div className="flex gap-2 min-w-max">
          {NOTIF_TYPES.map((t) => (
            <button
              key={t.id}
              onClick={() => setType(t.id)}
              className={`
                flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition
                ${type === t.id
                  ? 'bg-green-600 text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-neutral-700'
                }
              `}
            >
              <t.Icon className="w-4 h-4" />
              <span>{t.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Type Description */}
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {NOTIF_TYPES.find(t => t.id === type)?.description}
        </p>

        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Title
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Notification title…"
            className="w-full px-3 py-2.5 border border-gray-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition"
            maxLength={100}
          />
        </div>

        {/* Message */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Message
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Write your notification message…"
            rows={4}
            className="w-full px-3 py-2.5 border border-gray-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition resize-none"
            maxLength={500}
          />
          <p className="text-xs text-gray-400 mt-1">{message.length}/500</p>
        </div>

        {/* Sender Alias (for role_message type) */}
        {(type === "role_message" || type === "announcement") && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Send as
            </label>
            <div className="flex flex-wrap gap-2">
              {ROLE_PRESETS.map((r) => (
                <button
                  key={r.id}
                  onClick={() => setSenderAlias(r.id)}
                  className={`
                    px-3 py-1.5 rounded-full text-xs font-medium border transition
                    ${senderAlias === r.id
                      ? 'bg-green-600 text-white border-green-600'
                      : 'border-gray-200 dark:border-neutral-600 text-gray-600 dark:text-gray-300 hover:border-green-500'
                    }
                  `}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Target Users (for targeted type) */}
        {type === "targeted" && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Target Users
            </label>
            <UserSearch
              selectedUsers={selectedUsers}
              onAdd={(user) => setSelectedUsers(prev => [...prev, user])}
              onRemove={(id) => setSelectedUsers(prev => prev.filter(u => u.id !== id))}
            />
          </div>
        )}

        {/* Target Role (for announcements) */}
        {(type === "announcement" || type === "popup" || type === "role_message") && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Target Audience
            </label>
            <div className="flex gap-2">
              {TARGET_ROLES.map((r) => (
                <button
                  key={r.id}
                  onClick={() => setTargetRole(r.id)}
                  className={`
                    px-3 py-1.5 rounded-full text-xs font-medium border transition
                    ${targetRole === r.id
                      ? 'bg-green-600 text-white border-green-600'
                      : 'border-gray-200 dark:border-neutral-600 text-gray-600 dark:text-gray-300 hover:border-green-500'
                    }
                  `}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Priority */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Priority
          </label>
          <div className="flex gap-2">
            {PRIORITIES.map((p) => (
              <button
                key={p.id}
                onClick={() => setPriority(p.id)}
                className={`
                  px-3 py-1.5 rounded-full text-xs font-medium border transition
                  ${priority === p.id
                    ? 'bg-green-600 text-white border-green-600'
                    : 'border-gray-200 dark:border-neutral-600 text-gray-600 dark:text-gray-300 hover:border-green-500'
                  }
                `}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Theme */}
        {(type === "announcement" || type === "popup") && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Theme
            </label>
            <div className="flex gap-2 flex-wrap">
              {ANNOUNCEMENT_THEMES.map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => setTheme(opt.id)}
                  className={`
                    px-3 py-1.5 rounded-full text-xs font-medium border transition inline-flex items-center gap-2
                    ${theme === opt.id
                      ? 'bg-green-600 text-white border-green-600'
                      : 'border-gray-200 dark:border-neutral-600 text-gray-600 dark:text-gray-300 hover:border-green-500'
                    }
                  `}
                >
                  <span className={`w-2.5 h-2.5 rounded-full ${opt.swatch}`} />
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Optional icon and image */}
        {(type === "announcement" || type === "popup") && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Optional Icon Name
              </label>
              <input
                type="text"
                value={iconName}
                onChange={(e) => setIconName(e.target.value)}
                placeholder="e.g. Megaphone, Sparkles"
                className="w-full px-3 py-2.5 border border-gray-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition"
                maxLength={40}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Optional Image URL
              </label>
              <input
                type="url"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://..."
                className="w-full px-3 py-2.5 border border-gray-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition"
              />
            </div>
          </div>
        )}

        {/* Optional expiration */}
        {(type === "announcement" || type === "popup") && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Optional Expiration Time
            </label>
            <input
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="w-full md:w-auto px-3 py-2.5 border border-gray-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition"
            />
          </div>
        )}

        {/* Feedback Toggle */}
        <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-neutral-900 border border-gray-200 dark:border-neutral-700">
          <div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Request Feedback
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Users can respond with feedback when viewing this notification
            </p>
          </div>
          <button
            onClick={() => setRequiresFeedback(!requiresFeedback)}
            className={`
              relative w-10 h-6 rounded-full transition-colors
              ${requiresFeedback ? 'bg-green-600' : 'bg-gray-300 dark:bg-neutral-600'}
            `}
          >
            <span
              className={`
                absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform
                ${requiresFeedback ? 'translate-x-4' : 'translate-x-0.5'}
              `}
            />
          </button>
        </div>

        {requiresFeedback && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Feedback Prompt
            </label>
            <input
              type="text"
              value={feedbackPrompt}
              onChange={(e) => setFeedbackPrompt(e.target.value)}
              placeholder="What would you like to ask users?"
              className="w-full px-3 py-2.5 border border-gray-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition"
            />
          </div>
        )}

        {/* Send Button */}
        <button
          onClick={handleSend}
          disabled={sending || !title.trim() || !message.trim()}
          className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 dark:disabled:bg-neutral-600 text-white font-semibold rounded-lg transition-colors disabled:cursor-not-allowed"
        >
          {sending ? (
            <div className="orrith-loader orrith-loader-sm orrith-loader-on-dark" />
          ) : (
            <Send className="w-4 h-4" />
          )}
          {sending ? "Sending…" : "Send Notification"}
        </button>
      </div>
    </div>
  );
}

// ─── Sent Notification Card ─────────────────────────────────────
function SentNotificationCard({ notif, onDeactivate, onDelete }) {
  const timeAgo = notif.createdAt?.seconds
    ? formatTime(new Date(notif.createdAt.seconds * 1000))
    : "";

  const typeLabels = {
    announcement: { label: "Announcement", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" },
    targeted: { label: "Targeted", color: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400" },
    role_message: { label: "Role Message", color: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400" },
    popup: { label: "Pop-up", color: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" },
  };

  const t = typeLabels[notif.type] || typeLabels.announcement;

  return (
    <div className={`bg-white dark:bg-neutral-800 rounded-xl border border-gray-200 dark:border-neutral-700 p-4 ${!notif.isActive ? 'opacity-60' : ''}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${t.color}`}>
            {t.label}
          </span>
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
            notif.priority === 'urgent' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
            notif.priority === 'high' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
            'bg-gray-100 text-gray-600 dark:bg-neutral-700 dark:text-gray-400'
          }`}>
            {notif.priority}
          </span>
          {notif.senderAlias && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              as {notif.senderAlias}
            </span>
          )}
        </div>
        {notif.isActive && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => onDeactivate(notif.id)}
              className="text-xs text-gray-400 hover:text-red-500 transition"
              title="Deactivate"
            >
              <EyeOff className="w-4 h-4" />
            </button>
            <button
              onClick={() => onDelete(notif.id)}
              className="text-xs text-gray-400 hover:text-red-500 transition"
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
        {!notif.isActive && (
          <button
            onClick={() => onDelete(notif.id)}
            className="text-xs text-gray-400 hover:text-red-500 transition"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-1">
        {notif.title}
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
        {notif.message}
      </p>
      {notif.imageUrl && (
        <img
          src={notif.imageUrl}
          alt={notif.title || "Announcement image"}
          className="mb-3 w-full max-h-40 object-cover rounded-lg border border-gray-200 dark:border-neutral-700"
          loading="lazy"
        />
      )}

      {/* Stats */}
      <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
        <span className="flex items-center gap-1">
          <Eye className="w-3.5 h-3.5" />
          {notif.stats?.read || 0} read
        </span>
        <span className="flex items-center gap-1">
          <XCircle className="w-3.5 h-3.5" />
          {notif.stats?.dismissed || 0} dismissed
        </span>
        {notif.requiresFeedback && (
          <span className="flex items-center gap-1">
            <MessageCircle className="w-3.5 h-3.5" />
            {notif.stats?.feedbackCount || 0} feedback
          </span>
        )}
        <span className="flex items-center gap-1 ml-auto">
          <Clock className="w-3.5 h-3.5" />
          {timeAgo}
        </span>
      </div>
    </div>
  );
}

function formatTime(date) {
  if (!date) return "";
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return date.toLocaleDateString();
}

// ─── Main Page ──────────────────────────────────────────────────
export default function AdminNotifications() {
  const { user: adminUser } = useAdmin();
  const [sentNotifications, setSentNotifications] = useState([]);
  const [tab, setTab] = useState("compose");
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    destructive: false,
    loading: false,
    action: null,
  });
  const toast = useToast();

  useEffect(() => {
    const unsub = adminService.listenAdminNotifications((notifs) => {
      setSentNotifications(notifs);
    });
    return () => unsub();
  }, []);

  const handleDeactivate = async (id) => {
    setConfirmDialog({
      open: true,
      title: "Deactivate Notification",
      message: "Users will stop receiving this notification immediately.",
      destructive: true,
      loading: false,
      action: async () => {
        const snapshot = sentNotifications;
        setSentNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, isActive: false } : n)));
        try {
          await adminService.withAdminRetry(() => adminService.deactivateAdminNotification(adminUser, id));
          toast.success("Notification deactivated");
        } catch (err) {
          setSentNotifications(snapshot);
          toast.error(`Failed: ${err.message}`);
          throw err;
        }
      },
    });
  };

  const handleDelete = async (id) => {
    setConfirmDialog({
      open: true,
      title: "Delete Notification",
      message: "This permanently deletes the notification and cannot be undone.",
      destructive: true,
      loading: false,
      action: async () => {
        const snapshot = sentNotifications;
        setSentNotifications((prev) => prev.filter((n) => n.id !== id));
        try {
          await adminService.withAdminRetry(() => adminService.deleteAdminNotification(adminUser, id));
          toast.success("Notification deleted");
        } catch (err) {
          setSentNotifications(snapshot);
          toast.error(`Failed: ${err.message}`);
          throw err;
        }
      },
    });
  };

  const runConfirmAction = async () => {
    if (!confirmDialog.action) return;
    setConfirmDialog((prev) => ({ ...prev, loading: true }));
    try {
      await confirmDialog.action();
      setConfirmDialog({ open: false, title: "", message: "", destructive: false, loading: false, action: null });
    } catch (err) {
      errorDev("Operation failed:", err);
      setConfirmDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5 text-green-600 dark:text-green-400" />
              <h1 className="text-lg font-bold text-gray-900 dark:text-white">
                Orrith Announcements
              </h1>
            </div>
          </div>
          {/* Tab bar */}
          <div className="flex gap-1 pb-2">
            <button
              onClick={() => setTab("compose")}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
                tab === "compose"
                  ? "bg-green-600 text-white"
                  : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-neutral-800"
              }`}
            >
              Compose
            </button>
            <button
              onClick={() => setTab("sent")}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
                tab === "sent"
                  ? "bg-green-600 text-white"
                  : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-neutral-800"
              }`}
            >
              Sent ({sentNotifications.length})
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5 pb-24">
        {tab === "compose" ? (
          <NotificationForm onSent={() => setTab("sent")} />
        ) : (
          <div className="space-y-3">
            {sentNotifications.length === 0 ? (
              <div className="text-center py-16">
                <Bell className="w-12 h-12 text-gray-300 dark:text-neutral-600 mx-auto mb-3" />
                <p className="text-sm text-gray-500 dark:text-gray-400">No notifications sent yet</p>
              </div>
            ) : (
              sentNotifications.map((n) => (
                <SentNotificationCard key={n.id} notif={n} onDeactivate={handleDeactivate} onDelete={handleDelete} />
              ))
            )}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        destructive={confirmDialog.destructive}
        loading={confirmDialog.loading}
        confirmLabel={confirmDialog.destructive ? "Confirm" : "Continue"}
        onConfirm={runConfirmAction}
        onCancel={() => {
          if (confirmDialog.loading) return;
          setConfirmDialog({ open: false, title: "", message: "", destructive: false, loading: false, action: null });
        }}
      />
    </div>
  );
}
